import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiMaximize2, FiMessageSquare, FiPlus, FiSend, FiShield, FiTrash2, FiX } from 'react-icons/fi';
import { Avatar } from '../ui';
import { QIRA, GiaAvatar } from './Gia';
import { readStoredUser, useWorkspace } from '../Shell/Workspace';
import { useAssistant } from './assistantStore';
import './Robo.css';
import './fab.css';
import { t } from '../i18n';

const preview = (c) => { const last = [...c.content].reverse().find((m) => m.data); return last ? String(last.data).replace(/\s+/g, ' ').slice(0, 90) : ''; };

/**
 * A floating button at the end of the screen (everywhere except the GQ Assistant screen itself).
 * It opens a small chat: pick one of your conversations and carry on, or start a new one.
 */
export default function AssistantFab() {
  const { can } = useWorkspace();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const me = readStoredUser();
  const a = useAssistant();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('list'); // 'list' | 'chat'
  const [text, setText] = useState('');
  const [seen, setSeen] = useState(() => { try { return localStorage.getItem('gq-gia-hello') === '1'; } catch (e) { return false; } });
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const hidden = !me || pathname.startsWith('/robot') || !can('assistant');

  // while the panel is open the assistant counts as being looked at (no unread dot)
  useEffect(() => { if (!open || hidden) return undefined; a.watch(true); return () => a.watch(false); }, [open, hidden]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (open && view === 'chat') endRef.current?.scrollIntoView({ block: 'end' }); }, [open, view, a.convs, a.busy, a.active]);
  useEffect(() => { if (open && view === 'chat') setTimeout(() => inputRef.current && inputRef.current.focus(), 50); }, [open, view]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && !document.querySelector('.modal-overlay') && setOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  useEffect(() => { if (hidden) setOpen(false); }, [hidden]);
  useEffect(() => { // the hello bubble fades away on its own, and never comes back once she was opened
    if (seen) return undefined;
    if (open) { setSeen(true); try { localStorage.setItem('gq-gia-hello', '1'); } catch (e) { /* ignore */ } return undefined; }
    const h = setTimeout(() => { setSeen(true); try { localStorage.setItem('gq-gia-hello', '1'); } catch (e) { /* ignore */ } }, 9000);
    return () => clearTimeout(h);
  }, [open, seen]);
  if (hidden) return null;

  const send = (raw) => {
    const msg = (raw ?? text).trim();
    if (!msg || a.busy || a.outOfQuota) return;
    setText('');
    a.send(msg);
  };
  const openChat = (i) => { a.setActive(i); setView('chat'); };
  const startNew = () => { a.newChat(); setView('chat'); };
  const cur = a.current;
  const order = a.convs.map((c, i) => ({ c, i })).reverse(); // newest first

  return (
    <>
      {open && (
        <section className="fab-panel" role="dialog" aria-label={t("QIRA")}>
          <header className="fab-head">
            {view === 'chat' && <button className="icon-btn" onClick={() => setView('list')} aria-label={t("All chats")} title={t("All chats")}><FiArrowLeft className="fab-back" /></button>}
            <span className="fab-title"><GiaAvatar size={30} online /> <span className="fab-title-txt"><b>{view === 'chat' && cur && cur.content.length ? cur.name : t("QIRA")}</b>{!(view === 'chat' && cur && cur.content.length) && <small>{t("Your AI agent")}</small>}</span></span>
            <span className="grow" />
            <button className="icon-btn" onClick={startNew} aria-label={t("New chat")} title={t("New chat")}><FiPlus /></button>
            <button className="icon-btn" onClick={() => { setOpen(false); navigate('/robot'); }} aria-label={t("Open full screen")} title={t("Open full screen")}><FiMaximize2 /></button>
            <button className="icon-btn" onClick={() => setOpen(false)} aria-label={t("Close")} title={t("Close")}><FiX /></button>
          </header>

          {view === 'list' ? (
            <div className="fab-list">
              <button className="btn btn-primary fab-new" onClick={startNew}><FiPlus /> {t("New chat")}</button>
              {order.length === 0 && <p className="sub fab-empty">{t("No conversations yet.")}</p>}
              {order.map(({ c, i }) => (
                <div key={i} className={`fab-item ${i === a.active ? 'on' : ''}`} onClick={() => openChat(i)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && openChat(i)}>
                  <FiMessageSquare />
                  <span className="fab-item-txt"><b>{c.name}</b><small>{preview(c) || t("New chat")}</small></span>
                  <button className="icon-btn fab-del" aria-label={t("Delete chat")} title={t("Delete chat")} onClick={(e) => { e.stopPropagation(); a.remove(i); }}><FiTrash2 /></button>
                </div>
              ))}
              <p className="fab-note"><FiShield /> {t("Do not paste client data. E-mails, card numbers, keys and passwords are hidden automatically before anything is sent.")}</p>
            </div>
          ) : (
            <>
              <div className="fab-thread">
                {(!cur || cur.content.length === 0) ? (
                  <div className="fab-intro">
                    <img className="fab-intro-img" src={QIRA.bust} alt="QIRA" />
                    <b>{t("Hi, I'm QIRA")}</b>
                    <p className="sub">{t("Ask anything about your work.")}</p>
                  </div>
                ) : cur.content.map((m, i) => (
                  <div key={i} className={`gpt-msg ${m.question ? 'me' : 'bot'}`}>
                    {m.question ? <Avatar name={me.fullname} src={me.img_user} size={26} /> : <GiaAvatar size={28} />}
                    <div className={`gpt-bubble ${m.failed ? 'err' : ''}`}>{m.data}{m.note && <div className="gpt-note">{m.note}</div>}</div>
                  </div>
                ))}
                {a.busy && <div className="gpt-msg bot"><GiaAvatar size={28} /><div className="gpt-bubble typing"><i /><i /><i /></div></div>}
                <div ref={endRef} />
              </div>
              <div className="fab-foot">
                {a.quota && a.quota.daily > 0 && <span className={`gpt-quota ${a.outOfQuota ? 'out' : ''}`} title={t("Questions you can still ask today")}>{a.quota.used}/{a.quota.daily} {t("today")}</span>}
                <small className="sub"><FiShield /> {t("No client data")}</small>
              </div>
              {a.allowance && !a.allowance.unlimited && !a.allowance.enabled && <div className="gpt-blocked" role="alert">{t("QIRA is not enabled for your account. Ask an administrator to enable it and set your monthly amount.")}</div>}
              {a.allowance && !a.allowance.unlimited && a.allowance.enabled && a.allowance.percent >= 100 && <div className="gpt-blocked" role="alert">{t("You reached your monthly QIRA allowance. It renews on the 1st; an administrator can raise it.")}</div>}
              <form className="fab-input" onSubmit={(e) => { e.preventDefault(); send(); }}>
                <textarea ref={inputRef} rows={1} value={text} placeholder={t("Message QIRA…")} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
                <button className="btn btn-primary btn-icon" type="submit" disabled={a.busy || !text.trim() || a.outOfQuota} aria-label={t("Send")}><FiSend /></button>
              </form>
            </>
          )}
        </section>
      )}
      {!open && !seen && <span className="fab-hello" onClick={() => setOpen(true)}>{t("Hi, I'm QIRA — ask me anything")}</span>}
      <button className={`fab-btn ${open ? 'open' : ''}`} onClick={() => { setOpen((o) => !o); if (!open && !a.convs.length) setView('chat'); else if (!open) setView('list'); }} aria-label={open ? t("Close") : t("QIRA")} aria-expanded={open} title={t("Ask QIRA")}>
        {open ? <FiX /> : <img src={QIRA.avatar} alt="QIRA" draggable={false} />}
        {!open && !a.busy && <i className="fab-online" />}
        {!open && a.busy && <i className="fab-busy" />}
        {!open && !a.busy && a.unread > 0 && <i className="fab-dot">{a.unread > 9 ? '9+' : a.unread}</i>}
      </button>
    </>
  );
}
